import React from "react";

import { formatPrice } from "@/src/utils/formatPrice";

import {
  transactionEmailColumn,
  transactionNameColumn,
  transactionOfferColumn,
} from "./transactionColumns";
import DataTableColumnHeader from "../../../dataTable/DataTableColumnHeader";
import { dateColumn } from "../../../dataTable/dataTableColumns";

import type { TFunction } from "i18next";

import type { PurchaseRow } from "./purchaseColumns.types";
import type { PurchaseTransaction } from "../../../../api/StatsAPI.types";
import type { DataTableColumnDef } from "../../../dataTable/dataTable.types";

interface PurchaseColumnsOptions {
  t: TFunction;
  projectSlug: string | undefined;
  userAbility: boolean;
  offerAbility: boolean;
}

export function purchaseRow(purchase: PurchaseTransaction): PurchaseRow {
  const { user, offer } = purchase;

  return {
    purchase_uuid: purchase.purchase_uuid,
    user_uuid: purchase.user_uuid,
    offer_uuid: purchase.offer_uuid,
    email: user.email,
    name: `${user.first_name} ${user.last_name}`,
    price: purchase.price,
    currency: purchase.currency,
    offer_label: offer.label,
    purchase_type: purchase.purchase_type,
    rental_duration: purchase.rental_duration,
    purchase_date: purchase.purchase_date,
  };
}

export function purchaseColumns({
  t,
  projectSlug,
  userAbility,
  offerAbility,
}: PurchaseColumnsOptions): DataTableColumnDef<PurchaseRow>[] {
  return [
    transactionEmailColumn<PurchaseRow>(t, projectSlug, userAbility),
    transactionNameColumn<PurchaseRow>(t),
    {
      // Le tri porte sur le montant, pas sur le texte « prix devise ».
      accessorKey: "price",
      enableHiding: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("price")} />
      ),
      meta: { label: t("price"), width: "14%", minWidth: 110 },
      cell: ({ row }) => formatPrice(row.original.price, row.original.currency),
    },
    transactionOfferColumn<PurchaseRow>(t("title"), projectSlug, offerAbility),
    {
      accessorKey: "purchase_type",
      enableHiding: true,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("purchase_type_title")}
        />
      ),
      enableSorting: false,
      meta: {
        label: t("purchase_type_title"),
        width: "11%",
        minWidth: 85,
        filterOptions: ["tvod", "tvod_bundle"].map((type) => ({
          value: type,
          label: t(`purchase_type.${type}`),
        })),
      },
      cell: ({ row }) =>
        String(t(`purchase_type.${row.original.purchase_type}`)),
    },
    {
      // Le tri porte sur le nombre de jours (-1 : illimitée en premier).
      accessorKey: "rental_duration",
      enableHiding: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("rental_duration")} />
      ),
      meta: { label: t("rental_duration"), width: "9%", minWidth: 110 },
      cell: ({ row }) =>
        row.original.rental_duration < 0
          ? String(t("unlimited"))
          : String(t("day", { count: row.original.rental_duration })),
    },
    dateColumn<PurchaseRow>("purchase_date", t("purchase_date"), {
      width: "13%",
      minWidth: 160,
    }),
  ];
}
